/**
 * Logique PURE de navigation de la visionneuse dans l'ensemble ORDONNÉ complet
 * (bug A : préc./suiv. fiables sur TOUTES les images ; bug B : préchargement
 * directionnel des voisins). Séparée du hook/composant pour être testée sans DOM.
 *
 * Principe : la visionneuse connaît l'INDEX ABSOLU de l'asset courant dans
 * l'ensemble filtré/trié (fourni par la grille si chargé, sinon par l'endpoint de
 * position). Autour de cet index, on charge une FENÊTRE de voisins via la liste
 * existante — d'où les ids préc./suiv. ET les cibles de préchargement.
 */

/** Rayon de voisins qui doit être COUVERT (en mémoire) autour de l'index courant. */
export const NEIGHBOUR_RADIUS = 5;

/**
 * Rayon effectivement CHARGÉ quand la couverture est prise en défaut (audit A6).
 *
 * Charger exactement le rayon couvert condamnait à une requête PAR PAS : la fenêtre
 * glisse de 1, donc son nouveau bord manque toujours, et les 10 autres éléments étaient
 * re-téléchargés pour rien. En chargeant plus large que ce qu'on exige, un seul appel
 * couvre la dizaine de pas suivants (dans les DEUX sens) → ~10× moins de requêtes en
 * feuilletage rapide, pour un volume total transféré plus FAIBLE.
 */
export const NEIGHBOUR_FETCH_RADIUS = 15;
/** Profondeur de préchargement dans le SENS de déplacement (originaux). */
export const PRELOAD_AHEAD = 3;
/** Profondeur de préchargement dans le sens OPPOSÉ (moins probable). */
export const PRELOAD_BEHIND = 1;

/** Sens du dernier saut de navigation (pilote le préchargement directionnel). */
export type Direction = 'forward' | 'backward';

/**
 * Fenêtre `[offset, offset+limit)` à charger via la liste pour couvrir les voisins
 * de `index` (rayon `radius`), bornée à `[0, total)`. On charge un peu plus large
 * que strictement nécessaire pour amortir les allers-retours de navigation.
 */
export function windowRange(
  index: number,
  total: number,
  radius = NEIGHBOUR_RADIUS,
): { offset: number; limit: number } {
  if (total <= 0) return { offset: 0, limit: 0 };
  const clamped = Math.min(Math.max(0, index), total - 1);
  const offset = Math.max(0, clamped - radius);
  const end = Math.min(total, clamped + radius + 1);
  return { offset, limit: end - offset };
}

/** Vrai si `index` a un voisin précédent (index absolu). */
export function hasPrev(index: number): boolean {
  return index > 0;
}

/** Vrai si `index` a un voisin suivant dans l'ensemble de taille `total`. */
export function hasNext(index: number, total: number): boolean {
  return index >= 0 && index < total - 1;
}

/**
 * Indices absolus des ORIGINAUX à précharger autour de `index`, orientés par le
 * `direction` du dernier saut : `ahead` voisins dans le sens de déplacement +
 * `behind` dans l'autre. Exclut l'index courant (déjà affiché) et borne à
 * `[0, total)`. Ordonné du plus proche au plus lointain (priorité implicite),
 * sens de déplacement d'abord.
 */
export function preloadIndices(
  index: number,
  total: number,
  direction: Direction,
  ahead = PRELOAD_AHEAD,
  behind = PRELOAD_BEHIND,
): number[] {
  if (total <= 0 || index < 0 || index >= total) return [];
  const forward = direction === 'forward';
  const aheadStep = forward ? 1 : -1;
  const behindStep = -aheadStep;
  const out: number[] = [];
  const push = (i: number): void => {
    if (i >= 0 && i < total && i !== index && !out.includes(i)) out.push(i);
  };
  // Sens de déplacement d'abord (plus probable), du plus proche au plus lointain.
  for (let d = 1; d <= ahead; d += 1) push(index + aheadStep * d);
  for (let d = 1; d <= behind; d += 1) push(index + behindStep * d);
  return out;
}
